<template>
  <div id="app" @click="hideHeaderDropdown">
    <cw-header>
      <div slot="left-items">
        <a v-if="$route.name !== 'Home'" class="hidden-dashboard" @click="back">
          <span slot="trigger" class="icon is-medium has-text-primary">
            <i class="las la-2x la-angle-left" aria-hidden="true"></i>
          </span>
        </a>
        <img
          id="main-logo"
          class="hidden-full-router"
          src="./assets/red-cw-logo-no-text-inverted.svg"
          @click="$router.push('/')"
        />
      </div>
      <div v-if="universalFullscreenRoute" slot="content">
        <h1>{{ $route.name }}</h1>
      </div>
      <div v-else-if="capturingScreenshots" slot="content">
        <h1>Capstone Builders</h1>
        <h5>Robert O'Neil</h5>
      </div>
      <div v-else slot="content">
        <h1>{{ customer && customer.name }}</h1>
        <header-location-display v-if="authenticated"></header-location-display>
      </div>
      <div v-if="universalFullscreenRoute" slot="right-items">
        <router-link to="/" class="icon is-medium has-text-primary">
          <i class="las la-2x la-home" aria-hidden="true"></i>
        </router-link>
      </div>
      <div v-else slot="right-items" class="nav-right-actions">
        <a @click="refresh">
          <span class="icon is-medium has-text-primary">
            <i class="las la-2x la-sync" aria-hidden="true"></i>
          </span>
        </a>

        <!-- ===================== Dropdown ===================== -->
        <cw-dropdown
          :classes="[
            'header-dropdown',
            'is-right',
            'is-black',
            'modern-dropdown',
          ]"
          :isActive.sync="dropdownActive"
        >
          <!-- Trigger -->
          <a slot="trigger" class="icon is-medium has-text-primary">
            <i class="las la-2x la-ellipsis-h" aria-hidden="true"></i>
          </a>

          <!-- Account Section -->
          <router-link
            v-if="authenticated"
            to="/account"
            class="dropdown-item profile-section p-3"
          >
            <div class="media">
              <div class="media-left">
                <span
                  class="has-text primary is-flex is-align-items-center is-justify-content-center"
                  style="width: 48px; height: 48px;"
                >
                  <icon name="profile" scale="2"></icon>
                </span>
              </div>
              <div class="media-content">
                <p class="has-text-primary is-size-4 has-text-weight-medium">
                  {{ userInfo && userInfo.displayName }}
                </p>
                <p class="has-text-grey-light is-size-6">
                  {{ userInfo && userInfo.email }}
                </p>
              </div>
              <div class="media-right">
                <span class="icon has-text-primary is-size-5">
                  <i class="las la-angle-right has-text-primary"></i>
                </span>
              </div>
            </div>
          </router-link>
          <router-link v-else to="/login" class="dropdown-item has-text-danger">
            <span class="icon">
              <i class="las la-sign-in-alt"></i>
            </span>
            <span>Sign In</span>
          </router-link>

          <!-- Location Section (for authenticated users with multiple locations) -->
          <div
            v-if="authenticated && hasMultipleLocations"
            class="location-section mb-4"
          >
            <div class="location-header">
              <span class="icon location-icon is-size-4">
                <i class="las la-globe-americas"></i>
              </span>
              <span class="is-size-4 has-text-weight-medium">Service Area</span>
            </div>
            <div class="location-divider"></div>
            <a
              v-for="(location, index) in customerFamily"
              :key="location.customerId"
              class="dropdown-item location-item"
              :class="{
                'has-text-primary': location.customerId === selectedCustomerId,
              }"
              @click="selectLocation(location.customerId)"
            >
              <span class="icon check-icon">
                <i
                  v-if="location.customerId === selectedCustomerId"
                  class="las la-check"
                ></i>
              </span>
              <span>{{ getLocationName(location) }}</span>
              <div
                v-if="index < customerFamily.length - 1"
                class="location-item-divider"
              ></div>
            </a>
          </div>

          <!-- Action Buttons -->
          <div v-if="authenticated" class="action-buttons">
            <a
              class="icon-label is-horizontal has-divider"
              @click="showContact"
            >
              <span class="icon">
                <i class="las la-phone"></i>
              </span>
              <span>Contact</span>
            </a>
            <a
              v-if="canInviteUsers || canViewUsers"
              class="icon-label is-horizontal"
              @click="$modal.show('InviteUsers')"
            >
              <span class="icon">
                <i class="las la-plus"></i>
              </span>
              <span>Invite</span>
            </a>
          </div>

          <!-- Navigation Items -->
          <div v-if="authenticated" class="nav-buttons hidden-dashboard">
            <router-link to="/" class="icon-label is-vertical has-divider">
              <span class="icon">
                <i class="las la-home"></i>
              </span>
              <span>Home</span>
            </router-link>
            <router-link
              to="/projects"
              class="icon-label is-vertical has-divider"
            >
              <span class="icon">
                <i class="las la-map-marker-alt"></i>
              </span>
              <span>Projects</span>
            </router-link>
            <router-link
              to="/orders"
              class="icon-label is-vertical has-divider"
            >
              <span class="icon">
                <i class="las la-clipboard-list"></i>
              </span>
              <span>Orders</span>
            </router-link>
          </div>

          <!-- Dynamic dropdown actions -->
          <div v-if="dropdownActions.length > 0" class="hidden-dashboard">
            <a
              v-for="(item, index) in dropdownActions"
              :key="index"
              class="dropdown-item"
              @click="item.action()"
            >
              <span class="icon">
                <i class="fa" :class="item.icon()"></i>
              </span>
              <span>{{ item.title() }}</span>
            </a>
            <hr class="dropdown-divider" />
          </div>

          <!-- Scan Button -->
          <button
            v-if="authenticated"
            @click="$modal.show('scan')"
            class="button is-dark is-medium is-fullwidth scan-button"
          >
            <span class="icon is-size-4">
              <i class="las la-qrcode"></i>
            </span>
            <span class="is-size-5">Scan</span>
          </button>
        </cw-dropdown>
      </div>
    </cw-header>
    <div id="recaptcha-container"></div>
    <div v-if="browserIsIE" class="banner is-warning is-size-5">
      <span class="icon is-medium">
        <i class="las la-2x la-warning"></i>
      </span>
      <span
        >For best experience, view in
        <a href="https://www.google.com/chrome/" target="_blank"
          >Google Chrome</a
        ></span
      >
    </div>
    <div
      v-if="showServiceHold"
      class="banner is-warning is-size-5"
      @click="showServiceHoldInfo"
    >
      <span class="icon is-medium">
        <i class="las la-2x la-warning"></i>
      </span>
      <span>Account Service Hold</span>
    </div>
    <div
      v-if="upcomingMaintenance"
      class="banner is-warning is-size-5"
      style="position: relative; padding-right: 3rem;"
    >
      <span>
        <p class="has-text-weight-bold">
          {{ upcomingMaintenance.title || 'Scheduled Maintenance' }}
        </p>
        {{ upcomingMaintenance.message }}
      </span>
      <button
        @click="upcomingMaintenance = null"
        class="delete is-medium"
        style="position: absolute; right: 1rem; top: 50%; transform: translateY(-50%);"
        aria-label="Close maintenance banner"
      ></button>
    </div>
    <div
      v-if="updateAvailable"
      class="banner is-info is-size-5"
      style="position: relative; padding-right: 3rem;"
    >
      <span>
        <p class="has-text-weight-bold">
          {{ updateAvailable.title || 'Update Available' }}
        </p>
        {{ updateAvailable.message }}
      </span>
      <button
        @click="updateAvailable = null"
        class="delete is-medium"
        style="position: absolute; right: 1rem; top: 50%; transform: translateY(-50%);"
        aria-label="Close update banner"
      ></button>
    </div>

    <!--================= Mobile =================-->
    <mq-layout
      mq="sm"
      class="full-router"
      :class="{ 'full-height': !onCordova }"
    >
      <transition :name="routeTransition" mode="out-in">
        <router-view class="route-container"></router-view>
      </transition>
    </mq-layout>

    <mq-layout v-if="universalFullscreenRoute" mq="lg+">
      <router-view
        class="route-container universal-fullscreen-container"
      ></router-view>
    </mq-layout>
    <mq-layout v-else-if="!authenticated" mq="lg+">
      <cw-loader v-if="authenticating" />
      <cw-intro v-else />
    </mq-layout>
    <!--================= Dashboard =================-->
    <mq-layout
      v-else
      mq="lg+"
      class="dashboard"
      :class="{ 'lock-cordova-height': onCordova }"
    >
      <div class="dashboard-body">
        <div class="main-section box is-floating">
          <div class="list-routes">
            <router-link
              class="button is-black is-inverted is-medium"
              :class="{ 'is-current-route': listType === 'project' }"
              to="/projects"
              >Projects</router-link
            >
            <router-link
              class="button is-black is-inverted is-medium"
              :class="{ 'is-current-route': listType === 'order' }"
              to="/orders"
              >Orders</router-link
            >
            <router-link
              v-if="canViewInvoices"
              class="button is-black is-inverted is-medium"
              :class="{ 'is-current-route': listType === 'invoice' }"
              to="/invoices"
              >Invoices</router-link
            >
            <router-link
              v-if="canViewUsers"
              class="button is-black is-inverted is-medium"
              :class="{ 'is-current-route': listType === 'users' }"
              to="/users"
              >Users</router-link
            >
          </div>
          <div class="current-list">
            <keep-alive>
              <projects v-if="listType === 'project'"></projects>
            </keep-alive>
            <details-project
              v-if="listType === 'project-details'"
            ></details-project>
            <keep-alive>
              <orders v-if="listType === 'order'"></orders>
            </keep-alive>
            <keep-alive>
              <invoices v-if="listType === 'invoice'"></invoices>
            </keep-alive>
            <keep-alive>
              <users v-if="listType === 'users'"></users>
            </keep-alive>
          </div>
        </div>
        <div class="detail-section">
          <div class="actions">
            <a
              v-if="
                listType !== 'invoice' &&
                  listType !== 'users' &&
                  canCreateOrders
              "
              class="button is-black is-rounded is-outlined is-medium"
              @click="$modal.show('SetRequest')"
            >
              <span class="icon is-small">
                <i class="las la-plus"></i>
              </span>
              <span>Set New Box</span>
            </a>
            <a
              v-if="listType === 'users'"
              class="button is-black is-rounded is-outlined is-medium"
              @click="$modal.show('InviteUsers')"
            >
              <span class="icon is-small">
                <i class="las la-plus"></i>
              </span>
              <span>Invite Users</span>
            </a>
          </div>
          <div class="details" id="dash_details-section_details">
            <details-box
              v-if="detailsType === 'box'"
              :displayAsCard="true"
            ></details-box>
            <details-order
              class="detail-card box is-floating"
              v-if="detailsType === 'order'"
            ></details-order>
            <details-user
              class="detail-card box is-floating"
              v-if="detailsType === 'user'"
            ></details-user>
            <div
              class="detail-card box is-floating centerit"
              v-if="detailsType === 'none'"
            >
              <p class="has-text-grey-light">Select an item from the list</p>
            </div>
          </div>
        </div>
      </div>
    </mq-layout>
    <cw-footer v-if="!onCordova" />

    <!--================= Modals =================-->

    <modal name="scan" class="focus-modal">
      <div slot-scope="{ visible }">
        <cw-scan :visible="visible" />
      </div>
    </modal>

    <modal name="contact" class="focus-modal">
      <cw-contact></cw-contact>
    </modal>

    <modal name="SetRequest" class="focus-modal">
      <div class="fill-mobile">
        <div class="card box">
          <set-request></set-request>
        </div>
      </div>
    </modal>

    <modal name="PullRequest" class="focus-modal">
      <div slot-scope="{ params }" class="fill-mobile">
        <div class="card box">
          <pull-request
            :boxId="params.boxId"
            :type="params.type"
          ></pull-request>
        </div>
      </div>
    </modal>

    <div class="hidden-full-router">
      <modal
        name="AcceptAccount"
        class="focus-modal"
        @modal-toggle="v => modalToggled('AcceptAccount', v)"
      >
        <div class="card box is-foating">
          <div class="card-content">
            <accept-account></accept-account>
          </div>
        </div>
      </modal>

      <modal
        name="ResetPassword"
        class="focus-modal"
        @modal-toggle="v => modalToggled('ResetPassword', v)"
      >
        <div class="card box is-foating">
          <div class="card-content">
            <reset-password></reset-password>
          </div>
        </div>
      </modal>

      <modal
        name="Login"
        class="focus-modal"
        @modal-toggle="v => modalToggled('Login', v)"
      >
        <div class="card box is-floating">
          <div class="card-content">
            <cw-login></cw-login>
          </div>
        </div>
      </modal>

      <modal
        name="Register"
        class="focus-modal"
        :dismissOnBgTap="false"
        :showCloseButton="false"
        @modal-toggle="v => modalToggled('Register', v)"
      >
        <div class="card box is-floating">
          <div class="card-content">
            <cw-register></cw-register>
          </div>
        </div>
      </modal>

      <modal
        name="AccountDetails"
        class="focus-modal"
        @modal-toggle="v => modalToggled('AccountDetails', v)"
      >
        <div class="card box is-floating">
          <div class="card-content">
            <account-details></account-details>
          </div>
        </div>
      </modal>
    </div>

    <modal name="InviteUsers" class="focus-modal">
      <div class="fill-mobile">
        <div class="card box">
          <invite-users></invite-users>
        </div>
      </div>
    </modal>

    <modal name="RequestPasswordReset" class="focus-modal">
      <div class="fill-mobile">
        <div class="card box">
          <request-password-reset></request-password-reset>
        </div>
      </div>
    </modal>

    <modal name="ChangePassword" class="focus-modal">
      <div class="fill-mobile">
        <div class="card box">
          <change-password></change-password>
        </div>
      </div>
    </modal>

    <modal
      name="wifi-loader"
      class="wifi-loader-modal"
      :dismissOnBgTap="false"
      :showCloseButton="false"
    >
      <div class="hero" slot-scope="{ params }">
        <div class="hero-body has-text-centered">
          <div class="centerit">
            <div class="wifi-loader is-white"></div>
          </div>
          <p
            v-if="params && params.message"
            class="title is-size-4 is-padded-small has-text-white"
          >
            {{ params.message }}
          </p>
        </div>
      </div>
    </modal>

    <modal class="no-scroll" name="alert">
      <div slot-scope="{ params }">
        <cw-alert
          :title="params.title"
          :subtitle="params.subtitle"
          :message="params.message"
          :actions="params.actions"
          :type="params.type"
        ></cw-alert>
      </div>
    </modal>

    <modal
      class="no-scroll"
      name="blocking-alert"
      :dismissOnBgTap="false"
      :showCloseButton="false"
    >
      <div slot-scope="{ params }">
        <cw-alert
          :title="params.title"
          :subtitle="params.subtitle"
          :message="params.message"
          :actions="params.actions"
          :type="params.type"
        ></cw-alert>
      </div>
    </modal>

    <modal name="WhatsNew" :dismissOnBgTap="false" :showCloseButton="false">
      <whats-new></whats-new>
    </modal>

    <modal class="no-scroll" name="mfa-code-prompt">
      <div slot-scope="{ params }">
        <mfa-code-prompt
          :title="params.title"
          :message="params.message"
          :phoneNumber="params.phoneNumber"
          :errorMessage="params.errorMessage"
          :onVerify="params.onVerify"
          :onCancel="params.onCancel"
          :onResend="params.onResend"
        ></mfa-code-prompt>
      </div>
    </modal>

    <modal class="no-scroll" name="input-prompt">
      <div slot-scope="{ params }">
        <input-prompt
          :title="params.title"
          :message="params.message"
          :placeholder="params.placeholder"
          :inputType="params.inputType"
          :autocomplete="params.autocomplete"
          :confirmText="params.confirmText"
          :iconName="params.iconName"
          :iconClass="params.iconClass"
          :onConfirm="params.onConfirm"
          :onCancel="params.onCancel"
        ></input-prompt>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import authRequired from '@/components/mixins/auth-required';
import permissionsRequired from '@/components/mixins/permissions-required';
import screenshotConfig from '@/components/mixins/screenshot-config';

import axios from 'axios';
import browserIsIE from 'is-iexplorer';
import { Capacitor } from '@capacitor/core';

import Intro from '@/components/Intro';
import Scan from '@/components/blocks/Scan.vue';
import Login from '@/components/Login';
import Register from '@/components/Register';
import Support from '@/components/Support';
import AccountDetails from '@/components/DetailsAccount';
import SetRequest from '@/components/SetRequest';
import PullRequest from '@/components/PullRequest';
import InviteUsers from '@/components/blocks/InviteUsers';
import RequestPasswordReset from '@/components/blocks/RequestPasswordReset';
import AcceptAccount from '@/components/AcceptAccount';
import ResetPassword from '@/components/ResetPassword';
import ChangePassword from '@/components/blocks/ChangePassword';

import Contact from '@/components/blocks/Contact.vue';
import WhatsNew from '@/components/blocks/WhatsNew.vue';
import MFACodePrompt from '@/components/blocks/MFACodePrompt.vue';
import InputPrompt from '@/components/blocks/InputPrompt.vue';

// Event bus for coordinating component interactions
import Vue from 'vue';
export const EventBus = new Vue();

import { CUSTOMER_QUERY } from '@/graphql/constants';

import Projects from '@/components/Projects';
import Orders from '@/components/Orders';
import Invoices from '@/components/Invoices';
import Users from '@/components/Users';
import DetailsUser from '@/components/DetailsUser';
import DetailsProject from '@/components/DetailsProject';
import DetailsOrder from '@/components/DetailsOrder';
import DetailsBox from '@/components/DetailsBox';
import HeaderLocationDisplay from '@/components/HeaderLocationDisplay';
import { version } from '../package.json';

const apiHost = process.env.VUE_APP_API_HOST;

export default {
  name: 'app',

  components: {
    [Intro.name]: Intro,
    [Scan.name]: Scan,
    [Contact.name]: Contact,
    [MFACodePrompt.name]: MFACodePrompt,
    [InputPrompt.name]: InputPrompt,
    [WhatsNew.name]: WhatsNew,
    [Login.name]: Login,
    [Register.name]: Register,
    [Support.name]: Support,
    SetRequest,
    HeaderLocationDisplay,
    PullRequest,
    InviteUsers,
    RequestPasswordReset,
    ChangePassword,
    AcceptAccount,
    ResetPassword,
    AccountDetails,
    Projects,
    Orders,
    Invoices,
    Users,
    DetailsUser,
    DetailsProject,
    DetailsOrder,
    DetailsBox,
  },

  mixins: [authRequired, permissionsRequired, screenshotConfig],

  data() {
    return {
      dropdownActive: false,
      browserIsIE,
      routeModals: [
        'Login',
        'Register',
        'AccountDetails',
        'AcceptAccount',
        'ResetPassword',
      ],
      fullscreenRoutes: [
        'Support',
        'Privacy',
        'App',
        'EmailAction',
        'RequestAccountDeletion',
      ],
      upcomingMaintenance: null,
      updateAvailable: null,
      versionCheckFailures: 0,
      versionCheckRetryTimeout: null,
    };
  },

  computed: {
    ...mapState({
      dropdownActions: state => state.header.dropdownActions,
      appVersion: state => state.header.appVersion,
    }),
    ...mapState('locations', ['customerFamily', 'selectedCustomerId']),
    ...mapGetters('locations', ['hasMultipleLocations', 'currentLocation']),

    routeTransition() {
      return this.$router.$status.navigationType === 'forward'
        ? 'slide-left'
        : 'slide-right';
    },

    listType() {
      if (
        this.$route.name === 'ProjectDetails' ||
        this.$route.name === 'BoxDetails'
      ) {
        return 'project-details';
      } else if (
        this.$route.name === 'Orders' ||
        this.$route.name === 'OrderDetails'
      ) {
        return 'order';
      } else if (
        this.$route.name === 'Invoices' ||
        this.$route.name === 'InvoiceDetails'
      ) {
        return 'invoice';
      } else if (
        this.$route.name === 'Users' ||
        this.$route.name === 'UserDetails'
      ) {
        return 'users';
      } else {
        return 'project';
      }
    },

    detailsType() {
      if (this.$route.name === 'BoxDetails') return 'box';
      else if (this.$route.name === 'OrderDetails') return 'order';
      else if (this.$route.name === 'UserDetails') return 'user';
      else if (
        this.$route.name === 'Invoices' ||
        this.$route.name === 'InvoiceDetails'
      )
        return 'hidden';
      else return 'none';
    },

    showServiceHold() {
      return (
        this.customer &&
        this.customer.accountStatus &&
        this.customer.accountStatus !== 'Allow Service'
      );
    },

    universalFullscreenRoute() {
      // Return true if this is a route that should be displayed
      // outside of a logged-in context and focused full-screen
      return this.fullscreenRoutes.indexOf(this.$route.name) > -1;
    },

    onCordova() {
      return process.env.VUE_APP_PLATFORM === 'cordova';
    },
  },

  apollo: {
    customer: {
      query: CUSTOMER_QUERY,
      loadingKey: 'loadingCustomer',
      error(err) {
        window.console.error('auth-required.js - Error loading customer', err);
      },
      result({ data }) {
        this.$store.commit('user/setCustomer', { ...data });
      },
    },
  },

  watch: {
    userInfo() {
      this.$apollo.queries.customer.refetch();
    },
    $route() {
      this.checkRouteModals();
    },
    detailsType() {},
    $mq() {
      this.configBgClipForModals();
    },
    '$modal.visible': function() {
      this.configBgClipForModals();
    },
    // Watch for authentication changes
    authenticated(isAuthenticated) {
      if (isAuthenticated) {
        console.log('WhatsNew: Authentication detected, checking eligibility');
        // Wait for location data to be populated
        this.$nextTick(() => {
          // Additional delay to ensure locations are loaded
          setTimeout(() => this.checkWhatsNew(), 500);
        });
      }
    },
    // Watch for location changes
    hasMultipleLocations(hasMultiple) {
      // When locations change and user has multiple locations, check WhatsNew
      if (this.authenticated && hasMultiple) {
        console.log(
          'WhatsNew: Multiple locations detected, checking eligibility'
        );
        this.checkWhatsNew();
      }
    },
  },

  mounted() {
    this.checkRouteModals();
    this.startRefreshTimer();
    this.checkAppVersion();
    this.startMaintenanceCheckTimer();

    // Listen for location dropdown ready event
    this.$root.$on('location-dropdown-ready', () => {
      console.log('App: Received location-dropdown-ready event');
      this.checkWhatsNew();
    });

    // WhatsNew check will happen after auth/locations are loaded
    setTimeout(() => this.checkWhatsNew(), 1000);
  },

  destroyed() {
    // Clean up intervals
    if (this.maintenanceCheckInterval) {
      clearInterval(this.maintenanceCheckInterval);
    }
    if (this.versionCheckRetryTimeout) {
      clearTimeout(this.versionCheckRetryTimeout);
    }

    // Remove event listeners
    this.$root.$off('location-dropdown-ready');
  },

  methods: {
    startMaintenanceCheckTimer() {
      // Check for maintenance updates every 5 minutes
      this.maintenanceCheckInterval = setInterval(() => {
        this.checkAppVersion();
      }, 5 * 60 * 1000);
    },
    async checkAppVersion() {
      const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
      console.log(`[Version info] version : ${version}, platform: ${platform}`);
      try {
        const response = await axios.post(`${apiHost}/auth/v2/app-version`, {
          version: version || '1.0.0',
          platform,
        });

        // Reset failure counter on successful response
        this.versionCheckFailures = 0;

        // Clear any pending retry timeout
        if (this.versionCheckRetryTimeout) {
          clearTimeout(this.versionCheckRetryTimeout);
          this.versionCheckRetryTimeout = null;
        }

        // Handle maintenance info (always present in v2)
        if (response.data && response.data.maintenanceInfo) {
          const maintenance = response.data.maintenanceInfo;

          if (maintenance.status === 'upcoming') {
            this.upcomingMaintenance = maintenance;
          } else if (maintenance.status === 'active') {
            this.upcomingMaintenance = null;
          } else {
            this.upcomingMaintenance = null;
          }

          if (maintenance.status === 'active' && !maintenance.blockService) {
            this.$modal.show('alert', {
              title: maintenance.title || 'Maintenance in Progress',
              subtitle: maintenance.message,
              type: 'warning',
              actions: [
                {
                  title: 'Continue',
                  action: () => this.$modal.hide('alert'),
                },
              ],
            });
          }
        } else {
          this.upcomingMaintenance = null;
        }

        // Handle version info (new in v2)
        if (response.data && response.data.versionInfo) {
          const versionInfo = response.data.versionInfo;

          if (versionInfo.status === 'update_available') {
            // On web, reload the page to pick up the new deployment
            if (Capacitor.getPlatform() === 'web') {
              window.location.reload();
              return;
            }
            this.updateAvailable = versionInfo;
          } else {
            this.updateAvailable = null;
          }
        } else {
          this.updateAvailable = null;
        }
      } catch (err) {
        // Check if this is a server response error (4xx, 5xx)
        if (err.response && err.response.data) {
          let res = err.response.data;
          let defaultMessage = this.onCordova
            ? 'This version of RED Mobile is no longer supported. Please update your app.'
            : 'There has been an unknown error. Please contact Dispatch to place orders.';

          // Use blocking alert for errors (version incompatibility or blocked maintenance)
          this.$modal.show('blocking-alert', {
            title: res.title || 'Unsupported Version',
            subtitle: res.message || defaultMessage,
            type: 'danger',
            actions: [],
            maintenanceInfo: res.maintenanceInfo,
          });
        } else if (err.request) {
          // Network error - request was made but no response received
          console.warn('Network error checking app version:', err.message);

          // Track consecutive failures
          this.versionCheckFailures = (this.versionCheckFailures || 0) + 1;

          // Only show a non-blocking warning after multiple consecutive failures
          if (this.versionCheckFailures >= 3) {
            this.$modal.show('alert', {
              title: 'Connection Issue',
              subtitle:
                'Unable to check for app updates. Some features may not work correctly.',
              type: 'warning',
              actions: [
                {
                  title: 'OK',
                  action: () => this.$modal.hide('alert'),
                },
              ],
            });
          }

          // Schedule a retry with exponential backoff
          this.scheduleVersionCheckRetry();
        } else {
          // Something else went wrong (likely a code error)
          console.error('Error in checkAppVersion:', err);
        }
      }
    },

    scheduleVersionCheckRetry() {
      // Clear any existing retry timeout
      if (this.versionCheckRetryTimeout) {
        clearTimeout(this.versionCheckRetryTimeout);
      }

      // Calculate retry delay with exponential backoff
      // 1st failure: 10s, 2nd: 20s, 3rd: 40s, capped at 60s
      const baseDelay = 10000; // 10 seconds
      const maxDelay = 60000; // 60 seconds
      const retryDelay = Math.min(
        baseDelay * Math.pow(2, this.versionCheckFailures - 1),
        maxDelay
      );

      console.log(`Scheduling version check retry in ${retryDelay / 1000}s`);

      this.versionCheckRetryTimeout = setTimeout(() => {
        this.checkAppVersion();
      }, retryDelay);
    },

    configBgClipForModals() {
      let topModal =
        this.$modal.visible.length > 0 &&
        this.$modal.visible[this.$modal.visible.length - 1];
      if (
        this.$modal.visible.length > 0 &&
        (this.$mq !== 'sm' ||
          (this.$mq === 'sm' && this.routeModals.indexOf(topModal) < 0))
      ) {
        // disable background scrolling if a modal is visible and if this is not a routeModal
        let html = document.documentElement;
        html.classList.add('is-clipped');
      } else {
        let html = document.documentElement;
        html.classList.remove('is-clipped');
      }
    },

    back() {
      this.$router.back();
    },

    hideHeaderDropdown() {
      this.dropdownActive = false;
    },

    showContact() {
      this.$modal.show('contact');
    },
    selectLocation(customerId) {
      this.$store.dispatch('locations/selectLocation', customerId);
      // Navigate to home if not on projects or invoices page
      if (
        this.$route.path !== '/projects' &&
        this.$route.path !== '/invoices'
      ) {
        this.$router.push('/');
      } else {
        // Force refresh the page content
        this.$root.$emit('location-changed', customerId);
      }
    },
    getLocationName(location) {
      return (
        location.serviceArea ||
        (location.address && location.address.city) ||
        location.name
      );
    },

    checkRouteModals() {
      let routeModals = this.routeModals;
      let routeModal = null;
      routeModals.forEach(m => {
        if (this.$route.name === m) {
          routeModal = m;
        } else {
          this.$modal.hide(m);
        }
      });
      if (routeModal) this.$modal.show(routeModal);
    },

    modalToggled(routeName, visible) {
      if (this.$route.name === routeName && !visible) {
        if (this.user.authenticated) {
          this.$router.back();
        } else {
          this.$router.push('/');
        }
      }
    },

    showServiceHoldInfo() {
      this.$modal.show('alert', {
        title: 'Account Service Hold',
        message:
          'Your account is currently under service hold. You will not be able to submit any orders. Contact us to request that the service hold be lifted.',
        type: 'warning',
        actions: [
          {
            title: 'Ok',
            classes: 'is-black',
          },
          {
            title: 'Contact',
            classes: 'is-primary',
            callback: () => {
              this.$modal.show('contact');
              this.$modal.hide('alert');
            },
          },
        ],
      });
    },

    logOut() {
      this.$store.dispatch('user/logOut');
      this.$modal.show('alert', { title: 'Successfully logged out' });
      setTimeout(() => {
        this.$modal.hide('alert');
      }, 800);
      this.$router.push({ path: '/' });
    },

    startRefreshTimer() {
      setTimeout(() => {
        this.refresh();
        this.startRefreshTimer();
      }, 1200000); // Reload data every 20min
    },

    checkWhatsNew() {
      // Only proceed if we have completed authentication
      if (!this.authenticated) {
        return;
      }

      // Only show for users with multiple locations
      if (!this.hasMultipleLocations) {
        return;
      }

      // Check if this is a new version or first time seeing the feature
      const currentVersion = this.appVersion || '1.5.4';

      // Check if user has already dismissed this version
      const dismissedVersion = localStorage.getItem('whatsNewDismissed');
      console.log(
        `WhatsNew: Current version: ${currentVersion}, Dismissed version: ${dismissedVersion ||
          'none'}`
      );
      if (dismissedVersion === currentVersion) {
        console.log('WhatsNew: User has previously dismissed this version');
        return;
      }
      const lastSeenVersion = localStorage.getItem('lastSeenVersion');
      console.log(`WhatsNew: Last seen version: ${lastSeenVersion || 'never'}`);

      if (
        !lastSeenVersion ||
        this.shouldShowWhatsNew(lastSeenVersion, currentVersion)
      ) {
        console.log('WhatsNew: Showing modal for new version or first visit');
        // Show the modal after a brief delay to let the page load
        setTimeout(() => {
          // Find the location element
          const locationElement = document.querySelector(
            '.location-dropdown-target, .header-location-display'
          );

          if (locationElement) {
            this.$modal.show('WhatsNew');
            localStorage.setItem('lastSeenVersion', currentVersion);
            // WhatsNewDismissed will be set in the component when dismissed
          } else {
            // The modal will be shown when location-dropdown-ready event is fired
            // We've already set up the listener in mounted()
          }
        }, 1000);
      }
    },

    shouldShowWhatsNew(lastVersion, currentVersion) {
      // Show what's new if:
      // 1. No lastVersion (first time user)
      // 2. Versions are different (user has updated)
      const shouldShow = !lastVersion || lastVersion !== currentVersion;
      console.log(
        `WhatsNew: Version comparison - lastVersion: ${lastVersion ||
          'none'}, currentVersion: ${currentVersion}, shouldShow: ${shouldShow}`
      );
      return shouldShow;
    },

    refresh() {
      this.$broadcast().$emit('refresh-apollo');
    },

    // Manual trigger for testing - can be called from browser console
    showWhatsNewModal() {
      this.$modal.show('WhatsNew');
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus">

#recaptcha-container {
  display: none;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active  {
  transition: opacity .2s ease-out, transform .2s ease-out
}

.slide-left-enter, .slide-right-leave-to {
  transform: translateX(200px);
  opacity: 0;
}
.slide-left-leave-active, .slide-right-enter {
  transform: translateX(-200px);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active
  transition all .2s ease

.fade-enter, .fade-leave-active
  opacity 0

.nav-right-actions {
  display: flex
  & > *:not(:first-child){
    margin-left: 1rem
  }
}
.universal-fullscreen-container {
  min-height: 95vh
}

.upgrade-prompt {
  position: absolute;
  background-color: rgba(34, 30, 31, 0.8)
  z-index:10000
  top: 0
  left: 0
  height: 100vh
  width: 100vw
}

//  ======== Responsive Layout ========

$sm-breakpoint = 800px
$lg-breakpoint = 1250px

$dashboard-w = $sm-breakpoint
$dashboard-h = 700px

.full-router {

}
#main-logo {
  width: 8rem
  position: absolute
  top: 1.25rem
  left: 0
  cursor: pointer
}

@media (min-width:$dashboard-w) {

  #app {
    // height: 100vh
    position: relative;
    display: flex
    flex-direction: column
    justify-content: stretch
    align-items: stretch
    background-color: linear-gradient(0 #000, 100 #4B494B)
  }

  .cw-order-cell.box.is-floating {
    box-shadow: 0 0 0;
    border: 1px solid #bbb;
  }

  .full-router {
    display: none
  }

  .lock-cordova-height {
    height: 100vh - 5rem;
  }

  .dashboard {
    padding-bottom: 1rem;
    display: flex
    flex: 1 1 auto
    flex-direction: column
    align-items: stretch

    .stats {
      flex: 0 0 auto
      display: flex
      align-items: center
      padding: 2rem 0
      max-width: 50rem
      margin: auto
    }

    .stat {
      flex: 1 1 auto;
      margin: 0 1rem
    }

    .dashboard-body {
      flex: 1 1 auto
      display: flex
      flex-direction: row
      justify-content: center
      align-items: stretch

      $main-width = 50%
      $detail-width = 100% - $main-width
      $card-padding-lg-bkpt = 4rem

      .main-section {
        z-index: 2
        display: flex
        flex: 1 0 $main-width
        flex-direction: column
        width: $main-width
        max-width: 70rem
        margin: 1.5em

        .list-routes {
          .is-current-route {
            position: relative
            border-bottom-left-radius: 0
            border-bottom-right-radius: 0
            box-shadow: 0 0 0

              &::after {
              position: absolute
              content: ''
              width: 100%
              height: 5px
              left: 0;
              top:100% - @height
              border-radius: 999px
              background: red
              display: block
            }
          }
        }

        .current-list {
          flex-grow: 1
          padding: 2rem
          overflow: scroll

          .section {
            padding-left: 0
            padding-right: 0
          }
        }
      }


      .detail-section {
        $between-padding = 2.5rem
        display: flex
        margin: 1.5em
        width: $detail-width
        flex-direction: column
        flex: 0 1 $detail-width
        max-width: 30rem

        .actions {
          margin-bottom: $between-padding*0.5
          display: flex
          flex-direction: column
          align-items: stretch
          flex: 0 0 auto
        }
        .details {
          z-index: 1
        }
        .detail-card {
          flex: 0 1 auto
          min-height: 20rem
          overflow: scroll
          .section {
            padding: 0
          }

        }
      }
    }
  }
  .hidden-dashboard {
    display: none !important
  }
  .dropdown-item.hidden-dashboard {
    display: none !important
  }
  .dropdown-divider.hidden-dashboard {
    display: none
    border: 0
  }
}

@media (max-width:$dashboard-w - 1px) {
  .hidden-full-router {
    display: none
  }
  .dashboard {
    display: none
  }
}
/* Modern Dropdown Styles */
.modern-dropdown {
  .dropdown-menu {
    width: 400px !important;
    max-width: calc(100vw - 32px) !important;
    right: 0 !important;
    background: #1a1a1a !important;
    border-radius: 16px !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;

    @media (max-width: 800px) {
      width: 100vw !important;
      max-width: 100vw !important;
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      top: 100px !important;
      margin: 0 !important;
      border-radius: 0 !important;
    }
  }

  .dropdown-content {
    background: transparent !important;
    padding: 1rem !important;
    box-shadow: none !important;
  }

  // Profile section styles
  .profile-section {
    border-bottom: 1px solid #333;
    margin-bottom: 1rem;

    .media {
      align-items: center;
    }

    &:hover {
      background-color: transparent !important;

      .media {
        opacity: 0.8;
      }
    }
  }

  // Location section styles
  .location-section {

    border-bottom: 1px solid #333;
    padding-bottom: 1.5rem;

    .location-header {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;

      .location-icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ccc;
      }

    }

    .location-divider {
      height: 1px;
      background-color: #333;
      margin-left: 3.5rem;
      margin-bottom: 0.5rem;
    }

    .location-item {
      color: #ccc !important;
      padding-left: 1rem;
      position: relative;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
        border-radius: 6px;
      }

      &.is-active {
        color: #ff4757 !important;
      }

      .check-icon {
        width: 24px;
        color: #ff4757;
      }

      .location-item-divider {
        position: absolute;
        bottom: 0;
        left: 3.5rem;
        right: 0;
        height: 1px;
        background-color: #333;
      }
    }
  }

  .dropdown-item {
    color: #ccc !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      color: white !important;
    }
  }

  .dropdown-divider {
    background-color: #333 !important;
    margin: 0.5rem 0 !important;
  }

  // Action buttons section
  .action-buttons {
    border-bottom: 1px solid #333;
    margin-bottom: 1rem;
    display: flex;
    gap: 0;

    .icon-label {
      height: 60px;
    }
  }

  // Navigation buttons
  .nav-buttons {
    border-bottom: 1px solid #333;
    padding-bottom: 1rem;
    display: flex;
    gap: 0;

    .nav-button {
      flex-direction: column;
      padding: 1rem 0.5rem;

      .icon {
        margin-bottom: 0.25rem !important;
      }

      &:not(:last-child) {
        position: relative;

        &:after {
          content: '';
          position: absolute;
          right: 0;
          top: 20%;
          height: 60%;
          width: 1px;
          background-color: #333;
        }
      }
    }
  }

  // Flexible icon-label component
  .icon-label {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: #ccc;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
    padding: 0.75rem;
    background: none;
    border: none;
    font-size: 1.4em;
    font-weight: 500;

    .icon {
      font-size: 24px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &:hover {
      color: white;
      background-color: rgba(255, 255, 255, 0.1);
      border-radius: 6px;
    }

    // Vertical layout (default)
    &.is-vertical {
      flex-direction: column;
      padding: 1rem 0.5rem;

      .icon {
        margin-bottom: 0.5rem;
      }
    }

    // Horizontal layout
    &.is-horizontal {
      flex-direction: row;
      padding: 0.75rem 1rem;

      .icon {
        margin-right: 0.75rem;
        margin-bottom: 0;
      }
    }

    // With dividers
    &.has-divider:not(:last-child) {
      position: relative;

      &:after {
        content: '';
        position: absolute;
        right: 0;
        top: 20%;
        height: 60%;
        width: 1px;
        background-color: #333;
      }
    }

    // Active state
    &.is-active,
    &.router-link-active {
      color: #ff4757;
    }

    // Outlined variant
    &.is-outlined {
      border: 1px solid #333;
      border-radius: 6px;

      &:hover {
        border-color: #444;
      }
    }
  }

  // Scan button
  .scan-button {
    background-color: #444 !important;
    border: none !important;
    border-radius: 12px !important;
    padding: 1rem !important;

    &:hover {
      background-color: #555 !important;
    }
  }

  // Bulma button overrides for dropdown
  .button {
    &.is-dark {
      background-color: #363636 !important;
      border-color: #363636 !important;
      color: #f5f5f5 !important;

      &:hover {
        background-color: #2a2a2a !important;
        border-color: #2a2a2a !important;
      }

      &.is-outlined {
        background-color: transparent !important;
        border-color: #363636 !important;
        color: #f5f5f5 !important;

        &:hover {
          background-color: #363636 !important;
          color: #f5f5f5 !important;
        }
      }
    }

    &.is-medium {
      height: 60px !important;
    }
  }

  .buttons {
    gap: 0.5rem;

    .button {
      margin-bottom: 0 !important;
    }
  }

  .is-flex-grow-1 {
    flex-grow: 1;
  }
}
</style>
